import React, { useState } from 'react';
import { Send } from 'lucide-react';

const _a = btoa('https://26oblst31i.execute-api.us-east-1.amazonaws.com/default/CleanHouseNJ_EmailForwarder');
const _e = () => atob(_a);

type FormState = 'idle' | 'loading' | 'success' | 'error';

const QuoteForm: React.FC = () => {
  const [formState, setFormState] = useState<FormState>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [address, setAddress] = useState('');
  const [details, setDetails] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('loading');
    setErrorMsg('');

    try {
      const res = await fetch(_e(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, phone, email, serviceType, address, details }),
      });

      const data = await res.json();

      if (res.ok) {
        setFormState('success');
      } else {
        setFormState('error');
        setErrorMsg(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setFormState('error');
      setErrorMsg('Connection failed. Please try again.');
    }
  };

  return (
    <section id="quote-form" className="py-24 bg-brand-navy relative overflow-hidden">
      {/* Background Accents */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-brand-blue/10 skew-x-12 transform translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-1/4 h-1/2 bg-brand-red/5 -skew-x-12 transform -translate-x-1/2"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-5xl mx-auto bg-white rounded-[3rem] shadow-2xl overflow-hidden flex flex-col lg:flex-row">
          {/* Form Info */}
          <div className="w-full lg:w-2/5 bg-brand-gray-50 p-12 lg:p-16 flex flex-col justify-between">
            <div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-brand-navy mb-6">
                Get Your <span className="text-brand-blue">Free Quote</span>
              </h2>
              <p className="text-brand-navy/70 mb-8 leading-relaxed">
                Fill out the form and we'll get back to you within 2 hours with a personalized cleaning estimate.
              </p>

              <ul className="space-y-4">
                {['No hidden fees', 'Flexible scheduling', '100% Satisfaction Guarantee'].map((item) => (
                  <li key={item} className="flex items-center space-x-3 text-brand-navy font-medium">
                    <div className="w-6 h-6 bg-brand-blue/10 rounded-full flex items-center justify-center text-brand-blue">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-xs font-bold text-brand-navy/40 uppercase tracking-widest mb-2">Need immediate help?</p>
              <a href="tel:2018700452" className="text-2xl font-black text-brand-navy hover:text-brand-blue transition-colors">
                (201) 870-0452
              </a>
            </div>
          </div>

          {/* Actual Form */}
          <div className="w-full lg:w-3/5 p-12 lg:p-16">
            {formState === 'success' ? (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-4 py-12">
                <div className="text-5xl">🧹</div>
                <h3 className="text-2xl font-black text-brand-navy">Quote Request Received!</h3>
                <p className="text-brand-navy/60">We'll get back to you within 2 hours with your personalized estimate.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-brand-navy ml-1">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="John Doe"
                      disabled={formState === 'loading'}
                      className="w-full px-6 py-4 bg-brand-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all disabled:opacity-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-brand-navy ml-1">Phone Number *</label>
                    <input
                      type="tel"
                      required
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="(201) 000-0000"
                      disabled={formState === 'loading'}
                      className="w-full px-6 py-4 bg-brand-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all disabled:opacity-50"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-brand-navy ml-1">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="john@example.com"
                    disabled={formState === 'loading'}
                    className="w-full px-6 py-4 bg-brand-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all disabled:opacity-50"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-brand-navy ml-1">Service Type *</label>
                  <select
                    required
                    value={serviceType}
                    onChange={(e) => setServiceType(e.target.value)}
                    disabled={formState === 'loading'}
                    className="w-full px-6 py-4 bg-brand-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all appearance-none disabled:opacity-50"
                  >
                    <option value="">Select a service</option>
                    <option value="Standard Cleaning">Standard Cleaning</option>
                    <option value="Deep Cleaning">Deep Cleaning</option>
                    <option value="Move In / Move Out">Move In / Move Out</option>
                    <option value="Recurring Cleaning">Recurring Cleaning</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-brand-navy ml-1">Address (Optional)</label>
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="123 Washington St, Hoboken, NJ"
                    disabled={formState === 'loading'}
                    className="w-full px-6 py-4 bg-brand-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all disabled:opacity-50"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-brand-navy ml-1">Additional Details</label>
                  <textarea
                    rows={4}
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    placeholder="Tell us about your home (sq ft, number of rooms, specific needs...)"
                    disabled={formState === 'loading'}
                    className="w-full px-6 py-4 bg-brand-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all resize-none disabled:opacity-50"
                  ></textarea>
                </div>

                {formState === 'error' && (
                  <p className="text-red-500 text-sm text-center">{errorMsg}</p>
                )}

                <button
                  type="submit"
                  disabled={formState === 'loading'}
                  className="w-full bg-brand-red text-white py-5 rounded-2xl font-black text-lg shadow-xl hover:bg-red-700 transition-all transform hover:scale-[1.02] flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  <span>{formState === 'loading' ? 'Sending...' : 'Request My Cleaning Quote'}</span>
                  <Send className="w-5 h-5" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
